import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import SVG from 'react-inlinesvg';

import Button from '@common/components/button';
import svgIconChevron from '@common/assets/svg/icon-chevron.svg';

import { TRootState } from '@store/root-reducer';
import providerApi from '@utils/api';
import { useQuery } from 'react-query';

import CalendarSC, { MonthSelectorSC, DaySelectorSC } from './calendar.styled';
import userInterfaceStore from '@store/user-interface';
import HearthBeatLoader from '@common/components/loaders/heart-beat/hb-loader';
import { actions } from '@hilbi/web-widget/src/store/user-interface';

const dateFormat = 'YYYY-MM-DD';

const addZeroPrefix = (n: number) => (n < 10 ? `0${n}` : n);

const resolveClassName = (
  day: number,
  date: string,
  firstDay: number,
  isAvailable: boolean,
) => {
  const buff: string[] = ['day'];
  // some weird logic :)
  const abs = Math.abs(firstDay - 7);
  const weekends = [0, 1, 7, 8, 14, 15, 21, 22, 28, 29].map((it) => abs + it);

  // resolve "weekend" class
  if (weekends.includes(day)) {
    buff.push('weekend');
  }
  // resolve "selected" class
  if (day.toString(10) === moment(date).format('D')) {
    buff.push('selected');
  }

  if (!isAvailable) {
    buff.push('not-available');
  }

  return buff.join(' ');
};

type TMonthSelectorProps = {
  year: string;
  month: string;
  onChange: (year: string, month: string) => void;
};

const MonthSelector: React.FC<TMonthSelectorProps> = ({
  year,
  month,
  onChange,
}: TMonthSelectorProps) => {
  const mDate = moment(`${year}-${month}-01`);

  const setPrevMonth = () => {
    const prevDate = mDate.subtract(1, 'month');
    onChange(prevDate.format('YYYY'), prevDate.format('MM'));
  };
  const setNextMont = () => {
    const nextDate = mDate.add(1, 'month');
    onChange(nextDate.format('YYYY'), nextDate.format('MM'));
  };

  return (
    <MonthSelectorSC>
      <Button className="nav-btn" onClick={setPrevMonth}>
        <SVG height={12} src={svgIconChevron} />
      </Button>
      <span className="month-label">
        {mDate.format('MMMM')} {mDate.year()}
      </span>
      <Button className="nav-btn" onClick={setNextMont}>
        <SVG height={12} src={svgIconChevron} />
      </Button>
    </MonthSelectorSC>
  );
};

type TDaySelectorProps = {
  day: string;
  onChange: (date: string) => void;
};

const DaySelector: React.FC<TDaySelectorProps> = ({
  day,
  onChange,
}: TDaySelectorProps) => {
  const expert = useSelector(
    (store: TRootState) => store.userInterface.order.expert,
  );
  const medicalInstitution = useSelector(
    (store: TRootState) => store.userInterface.order.medicalInstitution,
  );
  const expertise = useSelector(
    (store: TRootState) => store.userInterface.order.expertise,
  );
  const { month, year } = useSelector(
    (store: TRootState) => store.userInterface.order.datetime,
  );

  const dispatch = useDispatch();

  const mDate = moment(`${year}-${month}-${day}`);

  const handleBtnClick = (day: number) => {
    onChange(`${mDate.format('YYYY-MM-')}${addZeroPrefix(day)}`);
    dispatch(actions.setDayClicked(true));
  };

  const params =
    `fromDate=${mDate.startOf('month').format(dateFormat)}` +
    `&toDate=${mDate.endOf('month').format(dateFormat)}` +
    `&expertId=${expert.id}&workspaceId=${medicalInstitution.id}`;

  const {
    isFetching: availableDaysLoading,
    data: availableDays,
  } = useQuery(['availableDays', month], async () => {
      const result = await providerApi.custom(
        `/e-calendar/e-visit/available-days?${params}`,
        {
          method: 'GET',
          data: {
            query: `${year}-${month}-${day}`,
            pagination: {
              page: 1,
              pageSize: 50,
              sortBy: 'lastName',
              direction: 'ASC',
            },
          },
        },
      );
      const dates = result.data.availableDays;

      return Object.keys(dates).map((item) => item);
  });

  const firstDay =
    mDate.startOf('month').day() === 0 ? 7 : mDate.startOf('month').day();

  return (
    <DaySelectorSC firstDayOfMonth={firstDay}>
      <div className="day-labels">
        <span>Po</span>
        <span>Út</span>
        <span>St</span>
        <span>Št</span>
        <span>Pi</span>
        <span>So</span>
        <span>Ne</span>
      </div>
      {availableDaysLoading && (
        <div className="days-loading">
          <HearthBeatLoader />
        </div>
      )}
      {!availableDaysLoading && (
        <div className="days">
          {Array.from(Array(mDate.daysInMonth()).keys()).map((d) => {
            const datetime = `${mDate.format('YYYY-MM')}-${addZeroPrefix(
              d + 1,
            )}`;
            const cmp = moment(datetime).startOf('day').format(dateFormat);
            const isAvailable = Boolean(
              availableDays && availableDays.includes(cmp),
            );

            return (
              <Button
                key={d}
                className={resolveClassName(
                  d + 1,
                  `${year}-${month}-${day}`,
                  firstDay,
                  isAvailable,
                )}
                onClick={() => (isAvailable ? handleBtnClick(d + 1) : null)}
              >
                <time dateTime={datetime}>{d + 1}</time>
                <div className="dots">
                  {isAvailable && <div className="green-dot" />}
                </div>
              </Button>
            );
          })}
        </div>
      )}
    </DaySelectorSC>
  );
};

const Calendar: React.FC = () => {
  const dispatch = useDispatch();

  const { year, month, day } = useSelector(
    (store: TRootState) => store.userInterface.order.datetime,
  );

  const handleChangeDay = useCallback(
    (date: string) => {
      if (`${year}-${month}-${day}` !== date) {
        dispatch(userInterfaceStore.setDate({ date }));
      }
    },
    [day, dispatch, month, year],
  );

  const handleChangeMonth = (year: string, month: string) => {
    dispatch(userInterfaceStore.setYearAndMonth({ year, month }));
  };

  return (
    <CalendarSC>
      <MonthSelector year={year} month={month} onChange={handleChangeMonth} />
      <DaySelector day={day} onChange={handleChangeDay} />
    </CalendarSC>
  );
};

export default Calendar;
